'use client';

import { useEffect, useRef, useState } from 'react';

import { editorStore } from '@/editor/store';
import {
  getLegacySceneScreenshotIdbKey,
  getProjectScopedSceneScreenshotIdbKey,
  getProjectScreenshotIdbKey,
  loadFromIndexedDB,
  openIndexedDB
} from '@/editor/store/sceneStore';

/**
 * Load a scene screenshot from IndexedDB (GenesysScreenshots store) and return an object URL.
 * Cancels on path/counter change or unmount; revokes object URLs when replaced or cleared.
 *
 * When `scenePath` changes, the previous object URL stays visible until the new path’s blob
 * loads (stale-while-revalidate), avoiding a flash of the fallback between scenes.
 *
 * When only `updateCounter` changes (same `scenePath`), the previous object URL stays visible
 * until the new blob is loaded, avoiding a flash of the fallback icon.
 *
 * @param scenePath - Scene path key (e.g. project-relative path) or null to skip loading
 * @param updateCounter - Optional counter to invalidate cache when screenshot is regenerated
 * @returns `{ url: string | null; isLoading: boolean }`
 */
export const useSceneScreenshot = (
  scenePath: string | null,
  updateCounter?: number
): { url: string | null; isLoading: boolean } => {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const prevScenePathRef = useRef<string | null>(null);
  const displayedUrlRef = useRef<string | null>(null);
  const projectFolder = editorStore.project.projectFolder;
  const projectKey = getProjectScreenshotIdbKey(projectFolder);

  const updateKey = updateCounter ?? 0;

  useEffect(() => {
    if (!scenePath) {
      if (displayedUrlRef.current) {
        URL.revokeObjectURL(displayedUrlRef.current);
        displayedUrlRef.current = null;
      }
      prevScenePathRef.current = null;
      setUrl(null);
      setIsLoading(false);
      return;
    }

    const pathChanged = prevScenePathRef.current !== scenePath;
    prevScenePathRef.current = scenePath;

    // On path change, keep the previous image visible until the new path’s load finishes
    // (see blob / catch handling below).

    let isCancelled = false;
    setIsLoading(true);

    const loadScreenshot = async () => {
      let db: IDBDatabase | null = null;
      try {
        db = await openIndexedDB();
        const sceneScreenshotKey = getProjectScopedSceneScreenshotIdbKey(projectFolder, scenePath);
        const legacySceneKey = projectFolder
          ? getLegacySceneScreenshotIdbKey(projectFolder, scenePath)
          : null;
        let blob = sceneScreenshotKey
          ? await loadFromIndexedDB(db, sceneScreenshotKey)
          : null;
        if (!blob && projectKey && legacySceneKey && legacySceneKey !== sceneScreenshotKey) {
          blob = await loadFromIndexedDB(db, legacySceneKey);
        }

        if (isCancelled) {
          return;
        }

        if (!blob) {
          // Counter-only refresh with no blob yet: keep previous screenshot visible.
          if (!pathChanged) {
            setIsLoading(false);
            return;
          }
          // New path has no screenshot: drop stale image from the previous path.
          const prev = displayedUrlRef.current;
          displayedUrlRef.current = null;
          setUrl(null);
          if (prev) {
            URL.revokeObjectURL(prev);
          }
          setIsLoading(false);
          return;
        }

        const newUrl = URL.createObjectURL(blob);
        if (isCancelled) {
          URL.revokeObjectURL(newUrl);
          return;
        }

        const prev = displayedUrlRef.current;
        displayedUrlRef.current = newUrl;
        setUrl(newUrl);
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        setIsLoading(false);
      } catch {
        if (!isCancelled) {
          if (pathChanged) {
            const prev = displayedUrlRef.current;
            displayedUrlRef.current = null;
            setUrl(null);
            if (prev) {
              URL.revokeObjectURL(prev);
            }
          }
          setIsLoading(false);
        }
      } finally {
        db?.close();
      }
    };

    void loadScreenshot();

    return () => {
      isCancelled = true;
    };
  }, [projectFolder, projectKey, scenePath, updateKey]);

  useEffect(() => {
    return () => {
      if (displayedUrlRef.current) {
        URL.revokeObjectURL(displayedUrlRef.current);
        displayedUrlRef.current = null;
      }
    };
  }, []);

  return { url, isLoading };
};
